export function isNumericLike(value: unknown): boolean {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value !== 'string') {
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) {
    return false;
  }

  return Number.isFinite(Number(trimmed));
}

export function normalizeChartRows<T extends Record<string, unknown>>(rows: T[]): T[] {
  return rows.map((row) => {
    let changed = false;
    const normalizedEntries = Object.entries(row).map(([key, value]) => {
      if (typeof value === 'number' && !Number.isFinite(value)) {
        changed = true;
        return [key, null];
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^(?:nan|[+-]?infinity)$/i.test(trimmed)) {
          changed = true;
          return [key, null];
        }
      }

      if (isNumericLike(value) && typeof value === 'string') {
        changed = true;
        return [key, Number(value.trim())];
      }

      return [key, value];
    });

  return changed ? Object.fromEntries(normalizedEntries) as T : row;
  });
}

export function hasFiniteNumericValueForKey(
  rows: Record<string, unknown>[],
  key: string,
): boolean {
  return rows.some((row) => {
    const value = row[key];
    return typeof value === 'number' && Number.isFinite(value);
  });
}

export function filterSeriesKeysWithNumericData(
  rows: Record<string, unknown>[],
  keys: string[],
): string[] {
  return keys.filter((key) => hasFiniteNumericValueForKey(rows, key));
}

export function computeTightNumericDomain(
  rows: Record<string, unknown>[],
  keys: string[],
): [number, number] | undefined {
  const values: number[] = [];

  for (const row of rows) {
    for (const key of keys) {
      const value = row[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        values.push(value);
      }
    }
  }

  if (values.length === 0) {
    return undefined;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  if (range === 0) {
    const padding = Math.max(Math.abs(max) * 0.01, 1);
    const domainMin = min >= 0 ? Math.max(0, min - padding) : min - padding;
    return [domainMin, max + padding];
  }

  const padding = Math.max(range * 0.15, Math.abs(max) * 0.005);
  const domainMin = min >= 0 ? Math.max(0, min - padding) : min - padding;
  return [domainMin, max + padding];
}

export function sanitizeNumericDomain(
  domain: unknown,
): [number, number] | undefined {
  if (!Array.isArray(domain) || domain.length !== 2) {
    return undefined;
  }

  const min = typeof domain[0] === 'number' ? domain[0] : Number(domain[0]);
  const max = typeof domain[1] === 'number' ? domain[1] : Number(domain[1]);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return undefined;
  }

  return [min, max];
}

function isDateLikeString(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  return (
    /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(trimmed) ||
    /^\d{1,2}\/\d{1,2}\/\d{2,4}(?:\s+\d{1,2}:\d{2}(?::\d{2})?\s?(?:AM|PM)?)?$/i.test(trimmed)
  );
}

function toSortableTimestamp(value: unknown): number | undefined {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : undefined;
  }

  if (typeof value === 'string' && isDateLikeString(value)) {
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : undefined;
  }

  return undefined;
}

export function sortChartRowsByXAxis<T extends Record<string, unknown>>(rows: T[], xAxisKey: string): T[] {
  if (rows.length < 2 || !xAxisKey) {
    return rows;
  }

  const decoratedRows = rows.map((row, index) => ({
    row,
    index,
    timestamp: toSortableTimestamp(row[xAxisKey]),
  }));

  if (decoratedRows.some((entry) => entry.timestamp === undefined)) {
    return rows;
  }

  return [...decoratedRows]
    .sort((a, b) => (a.timestamp as number) - (b.timestamp as number) || a.index - b.index)
    .map((entry) => entry.row);
}
